import React from 'react';
import { Users, X } from 'lucide-react';
import { getText } from '@/translate/text';

const buildSelectedPills = (formData, optionsMap) => {
  const order = [
    'subscription_status',
    'activity_status',
    'gender',
    'region',
    'target_condition',
    'age_group',
  ];

  const pills = [];

  if (formData.audience_type === 'single_users') {
    pills.push({
      field: 'users',
      label: `${formData.audience_list?.length || 0} Users`,
    });
  } else {
    for (const field of order) {
      const v = formData[field];
      if (!v) continue;

      const opts = optionsMap[field] || [];
      const found = opts.find((o) => o.value === v);

      pills.push({
        field,
        value: v,
        label: found ? found.label : String(v),
      });
    }
  }

  return pills;
};

const Pill = ({ label, onRemove, enableRemoveFilter = true }) => {
  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-[#008236] text-white rounded-xl text-sm font-medium">
      {label}
      <button
        type="button"
        onClick={onRemove}
        className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
      >
        {enableRemoveFilter && <X className="w-3 h-3 opacity-70" />}
      </button>
    </div>
  );
};

export const EstimatedRecipientsBar = ({
  formData,
  audienceOptions,
  estimatedUsers,
  estimateLoading,
  estimateError,
  onRemoveFilter,
  enableRemoveFilter = true,
}) => {
  const pills = React.useMemo(() => {
    return buildSelectedPills(formData, audienceOptions);
  }, [formData, audienceOptions]);

  return (
    <div className="p-8 bg-[#F0FDF4] rounded-3xl border border-[#D1FAE5] dark:bg-emerald-900/10 dark:border-emerald-800/20 transition-all">
      {formData.audience_type === 'user_group' ? (
        <div className="flex justify-between items-center ">
          <div className="flex flex-wrap gap-2 max-w-xl">
            {pills.length > 0 &&
              pills.map((p) => (
                <Pill
                  key={p.field + (p.value || '')}
                  label={p.label}
                  onRemove={() => {
                    if (p.field === 'users') {
                      onRemoveFilter?.('users');
                    } else {
                      onRemoveFilter?.(p.field);
                    }
                  }}
                  enableRemoveFilter={enableRemoveFilter}
                />
              ))}
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right text-[#008236]">
              <p className="font-semibold text-[20px] leading-[20px] tracking-[-0.15px] opacity-80 mb-1">
                {getText('estimated_receipients') || 'Estimated Recipients'}
              </p>
              <div className="font-bold text-[24px] leading-[32px] tracking-[0.07px]">
                {estimateLoading ? (
                  <span className="text-lg opacity-70 animate-pulse">...</span>
                ) : (
                  (estimatedUsers ?? '-')
                )}
              </div>
              {estimateError && <p className="text-xs text-red-500 mt-1">{estimateError}</p>}
            </div>
            <Users className="w-12 h-12 opacity-80" color="#008236" />
          </div>
        </div>
      ) : (
        <div className="flex justify-between">
          <div className="text-sm text-[#008236]">
            <p className="font-semibold text-[20px] leading-[20px] tracking-[-0.15px] opacity-80 mb-1">
              {getText('estimated_receipients') || 'Estimated Recipients'}
            </p>
            <div className="font-bold text-[24px] leading-[32px] tracking-[0.07px]">
              {formData.audience_list.length ?? 0}
            </div>
          </div>
          <Users className="w-12 h-12 opacity-80" color="#008236" />
        </div>
      )}
    </div>
  );
};
